import {
  ChangeEventHandler,
  MouseEventHandler,
  useRef,
  useCallback,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  MutableRefObject,
} from "react";
import SliceIcon from "../../assets/slice.svg";
import PhotoIcon from "../../assets/photo.svg";
import DeleteIcon from "../../assets/delete.svg";
import CloseIcon from "../../assets/close.svg";

import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil";
import styles from "./index.module.css";
import {
  logInfoAtom,
  showLoginAtom,
  haveSearchTabAtom,
  cardsTypeAtom,
} from "@/store";
import { trackEvent } from "@/utils/trackEvent"
import { isHasAlpha2, rodinUploadCompressImg } from "@/utils/format";
import { useTranslation } from "react-i18next";
import { useTips } from "@/common/GlobalTips";
import { searchEmbedding } from "@/utils/net";
import { ModelLoading } from "@/common/ModelLoading";
import { useParams } from "react-router-dom";

// interface TransitionDocument extends Document {
//   startViewTransition?: (callback: () => void) => {
//     finished: Promise<void>;
//     ready: Promise<void>;
//   };
// }

interface IProps {}

const ModelSearchBox = (
  _: IProps,
  ref: MutableRefObject<{
    loadMore: (page_num: string) => Promise<any[]>;
  }>
) => {
  const wrapper = useRef<HTMLDivElement>();
  const inputRef = useRef<HTMLInputElement>();
  const tip = useTips();
  const { t } = useTranslation();
  const inTransition = useRef(false);
  const [loading, setLoading] = useState(false);
  const params = useParams();

  const onFold = useCallback((e: MouseEvent) => {
    if (wrapper.current) {
      const rect = wrapper?.current?.getBoundingClientRect();
      const inWrapper =
        e.x > rect.x && e.x < rect.right && e.y > rect.top && e.y < rect.bottom;
      if (!inWrapper) {
        requestAnimationFrame(() => {
          if (document.startViewTransition) {
            wrapper.current &&
              wrapper.current.classList.add(styles.limitWrapper);
            const view = document.startViewTransition(() => {
              wrapper.current?.classList.remove(styles.expanded);
            });
            view.finished.then(() => {
              wrapper.current?.classList.remove(styles.limitWrapper);
            });
          } else {
            wrapper.current.classList.remove(styles.expanded);
          }
        });
        window.removeEventListener("pointerdown", onFold);
      }
    } else {
      window.removeEventListener("pointerdown", onFold);
    }
  }, []);

  const onExpand = useCallback(() => {
    if (document.startViewTransition) {
      wrapper.current.classList.add(styles.limitWrapper);
      const view = document.startViewTransition(() => {
        wrapper.current.classList.add(styles.expanded);
        inputRef.current.focus();
      });
      view.finished.then(() => {
        wrapper.current.classList.remove(styles.limitWrapper);
      });
    } else {
      wrapper.current.classList.add(styles.expanded);
      inputRef.current.focus();
    }
    trackEvent(`Rodin - Search: Open`)
    window.addEventListener("pointerdown", onFold);
  }, []);

  const onPointerOut = () => {
    if (
      wrapper.current.classList.contains(styles.buttonHover) &&
      !inTransition.current
    ) {
      if (document.startViewTransition) {
        wrapper.current.classList.remove(styles.wrapper);
        const view = document.startViewTransition(() => {
          wrapper.current?.classList.remove(styles.buttonHover);
        });
        view.finished.then(() => {
          wrapper.current?.classList.add(styles.wrapper);
        });
      } else {
        wrapper.current.classList.remove(styles.buttonHover);
      }
    }
  };

  const setShowLogin = useSetRecoilState(showLoginAtom);
  const fileInputRef = useRef<HTMLInputElement>();

  const onUpload = () => {
    inputRef.current.focus();
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  const onPointerIn: MouseEventHandler<HTMLDivElement> = (initEvent) => {
    if (!wrapper.current.classList.contains(styles.buttonHover)) {
      if (document.startViewTransition) {
        let mouseCoordinate = { x: initEvent.clientX, y: initEvent.clientY };
        const callback = (e) => {
          mouseCoordinate = { x: e.clientX, y: e.clientY };
        };
        document.addEventListener("mousemove", callback);
        const openLink = (e) => {
          const rect = wrapper?.current?.getBoundingClientRect();
          const isInTag =
            rect &&
            e.clientX > rect.x &&
            e.clientX < rect.right &&
            e.clientY > rect.top &&
            e.clientY < rect.bottom;
          if (isInTag) {
            onUpload();
          }
        };
        document.addEventListener("mousedown", openLink);
        wrapper.current.classList.remove(styles.wrapper);
        const trans = document.startViewTransition(() => {
          wrapper.current.classList.add(styles.buttonHover);
        });
        trans.ready.then(() => {
          inTransition.current = true;
        });
        trans.finished.then(() => {
          inTransition.current = false;
          const rect = wrapper?.current?.getBoundingClientRect();
          const isInTag =
            rect &&
            mouseCoordinate.x > rect.x &&
            mouseCoordinate.x < rect.right &&
            mouseCoordinate.y > rect.top &&
            mouseCoordinate.y < rect.bottom;
          if (!isInTag) {
            onPointerOut();
          } else {
            wrapper.current.classList.add(styles.wrapper);
          }
          document.removeEventListener("mousemove", callback);
          document.removeEventListener("mousedown", openLink);
        });
      } else {
        wrapper.current.classList.add(styles.buttonHover);
      }
    }
  };

  const [uploadedImg, setUploadedImg] = useState<{
    data: string;
    file: File;
  }>();

  const handlerFiles = async (fileList) => {
    let newImages;
    newImages = fileList.map((i) => i.file);
    // Add new filter to only allow png, jpg, jpeg images
    const unsupportedFormatFiles = newImages.some(
      (file) =>
        !["image/png", "image/jpg", "image/jpeg", "image/webp"].includes(
          file.type
        )
    );
    if (unsupportedFormatFiles) {
      tip({
        type: "warning",
        content: t("TIP_WRAN_IMG_NO_SUPPORT"),
      });
      return;
    }

    if (newImages.length) {
      const processedImagesPromises = newImages.map((file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        return new Promise((resolve) => {
          reader.onload = (e) => {
            resolve({
              data: e.target.result,
              file,
            });
          };
        });
      });
      Promise.all(processedImagesPromises).then(([data]) => {
        setUploadedImg(data);
        setLoading(false);
      });
    }
  };

  const handleFile = async (files: FileList) => {
    if (!logInfo) {
      window.removeEventListener("pointerdown", onFold);
      wrapper.current.classList.add("hidden");
      setTimeout(() => {
        wrapper.current && wrapper.current.classList.remove(styles.expanded);
        wrapper.current && wrapper.current.classList.remove("hidden");
      }, 1000);
      setShowLogin(true);
      return;
    }
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        setLoading(true);
      });
    } else {
      setLoading(true);
    }

    const newImages = [...files].filter((file) =>
      file.type.startsWith("image/")
    );
    if (newImages.length === 0) {
      tip({
        type: "error",
        content: t("TIP_ERR_NEWRODIN_IMG_NO_VALID"),
      });
      return;
    }
    tip({
      type: "primary",
      content: t("TIP_PRIMARY_NEWRODIN_IMG_PROCESSED"),
    });

    const arr = await Promise.all(newImages.map((item) => isHasAlpha2(item)));
    rodinUploadCompressImg(newImages, arr).then(({ fileData }) => {
      const oversizedFiles = fileData.some(
        (file) => file.file.size > 3 * 1024 * 1024
      );
      if (oversizedFiles) {
        tip({
          type: "warning",
          content: t("TIP_WRAN_NEWRODIN_UPLOAD_COMPRESS"),
        });
        return;
      }

      handlerFiles(fileData);
    });
  };
  const handleFileInputChange: ChangeEventHandler<HTMLInputElement> = async (
    e
  ) => {
    trackEvent('Rodin - Search - Image: Uploading')
    const files = e.target.files;
    handleFile(files);
  };

  const [haveSearchTab, _setHaveSearchTab] =
    useRecoilState<boolean>(haveSearchTabAtom);
  const haveSearchTabRef = useRef<boolean>(haveSearchTab);
  const setHaveSearchTab = useCallback((props: boolean) => {
    haveSearchTabRef.current = props;
    _setHaveSearchTab(props);
  }, []);
  const handlerDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    wrapper.current.classList.add(styles.fileDrop);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    wrapper.current.classList.add(styles.fileDrop);
  };

  const handlerDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    wrapper.current.classList.remove(styles.fileDrop);
  };

  const [searching, setSearching] = useState(false);
  const cardsType = useRecoilValue(cardsTypeAtom);

  const formDataRef = useRef<FormData>();
  const logInfo = useRecoilValue(logInfoAtom);

  const search = async (page_num = "0") => {
    if (formDataRef.current) {
      try {
        formDataRef.current.set("page_num", page_num);
        const res = await searchEmbedding({
          formData: formDataRef.current,
          use_bff: !logInfo,
        });
        if (res?.data) {
          if (res.data.length) {
            setSearching(false);
            return res.data;
          }
        } else {
          tip({
            type: "error",
            presetText: "ERROR",
            content: "Failed to fetch search results",
          });
          throw new Error("Failed to fetch search results.");
        }
      } finally {
        setSearching(false);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    loadMore: search,
  }));

  const handleSearch = async () => {
    trackEvent('Rodin - Search with Image')
    setSearching(true);
    const formData = new FormData();
    formData.append("images", uploadedImg.file);
    formData.append("task_type", "Rodin");
    formData.set("type", cardsType as string);
    formDataRef.current = formData;
    setHaveSearchTab(true);
  };

  const [searchWords, setSearchWords] = useState<string>();

  const handleSearchKeywords = async (keyword?: string) => {
    if (keyword) {
      const url = new URL(window.location.href);
      trackEvent('Rodin - Search - Keyword', { keyword:keyword })
      url.pathname = "/rodin/search/" + keyword;
      // window.history.pushState(null, null, url);
      setSearchWords(keyword);
      setSearching(true);
      const formData = new FormData();
      formData.set("keyword", keyword);
      formData.append("task_type", "Rodin");
      formData.set("type", cardsType as string);
      formDataRef.current = formData;
      setHaveSearchTab(true);
    } else {
      setHaveSearchTab(false);
    }
  };

  useEffect(() => {
    const keyword = params?.keyword ?? "";
    if (keyword) {
      handleSearchKeywords(keyword);
    }
  }, []);

  useEffect(() => {
    if (haveSearchTab) {
      return () => {
        if (haveSearchTabRef.current) {
          setUploadedImg(undefined);
          formDataRef.current = null;
          if (document.startViewTransition) {
            document.startViewTransition(() => {
              setSearchWords(undefined);
              setHaveSearchTab(false);
            });
          } else {
            setHaveSearchTab(false);
            setSearchWords(undefined);
          }
        }
      };
    }
  }, [haveSearchTab, cardsType]);

  if (loading) {
    return (
      <div
        className={
          "flex w-[158px] h-[158px] justify-center items-center rounded-2xl bg-[rgba(255,255,255,0.1)] absolute bottom-[14.5px] right-24 " +
          styles.wrapper
        }
      >
        <ModelLoading
          color
          type="rodin"
        />
      </div>
    );
  }

  if (haveSearchTab) {
    return (
      <div
        className={
          "h-[42px] flex bg-[rgba(22,136,143,1)] rounded-xl p-[5px] justify-center items-center hover:bg-[rgba(40,221,232,0.1)] border-[rgba(40,221,232,0.1)] hover:border-[rgba(40,221,232,0.6)] border-[1px] mr-3 text-white " +
          styles.thumbnailWrapper +
          " " +
          styles.limitWrapper
        }
      >
        {searchWords && <div className="ml-[14px]">{searchWords}</div>}
        {uploadedImg && (
          <img
            alt="uploaded"
            src={uploadedImg?.data}
            className="w-8 h-8 rounded-l-[10px] rounded-r-[6px]"
          />
        )}
        <img
          src={CloseIcon}
          alt="close"
          className="w-[14px] h-[14px] ml-[10px] mr-[11px] cursor-pointer"
          onClick={() => {
            setUploadedImg(undefined);
            if (document.startViewTransition) {
              document.startViewTransition(() => {
                setSearchWords(undefined);
                setHaveSearchTab(false);
              });
            } else {
              setHaveSearchTab(false);
              setSearchWords(undefined);
            }

            const url = new URL(window.location.href);
            url.pathname = `/rodin`;
            // window.history.pushState(null, null, url);
          }}
        />
        {uploadedImg?.data && (
          <div
            className={
              "hidden p-[6px] justify-center items-center rounded-2xl bg-[rgba(255,255,255,0.1)] absolute bottom-[65px] " +
              styles.imageThumbnail
            }
          >
            <img
              src={uploadedImg?.data}
              className="w-[120px] h-[120px] object-cover rounded-xl bg-[rgba(255,255,255,0.1)]"
            />
          </div>
        )}
      </div>
    );
  }

  if (uploadedImg) {
    return (
      <div
        className={
          "flex p-2 justify-center items-center rounded-2xl bg-[rgba(255,255,255,0.1)] absolute bottom-[14.5px] right-24 flex-col gap-[7px] " +
          styles.wrapper +
          " " +
          styles.limitWrapper +
          " " +
          styles.imageWrapper
        }
      >
        <img
          src={uploadedImg.data}
          className={
            "w-[120px] h-[120px] object-cover rounded-xl bg-[rgba(255,255,255,0.1)] "
          }
        />
        {!searching && (
          <div
            onClick={() => {
              setHaveSearchTab(false);
              trackEvent(`Rodin - Search - Image: Delete`)
              if (document.startViewTransition) {
                setLoading(true);
                setUploadedImg(undefined);
                const view = document.startViewTransition(() => {
                  setLoading(false);
                });
                view.finished.then(() => {
                  onExpand();
                });
              } else {
                setUploadedImg(undefined);
              }
            }}
            className={
              "absolute right-[14px] top-[14px] rounded-full w-7 h-7 bg-[rgba(255,255,255,0.2)] border-[rgba(0,0,0,0)] flex justify-center items-center cursor-pointer border-[1px] hover:border-white shadow-[rgba(0,0,0,0.4)] " +
              styles["delete-button"]
            }
          >
            <img src={DeleteIcon} />
          </div>
        )}

        <div className="w-full flex justify-center">
          <div
            className={
              "bg-[rgba(40,221,232,0.1)] border-[rgba(0,0,0,0)] rounded-[31px] h-7 flex items-center justify-center text-[rgba(40,221,232,1)] hover:border-[rgba(40,221,232,0.4)] border-[1px] cursor-pointer font-bold w-[120px] " +
              styles["search-button"]
            }
            onClick={handleSearch}
          >
            {searching ? (
              <>
                <span className="loading-text-animation text-screen-sm">
                  &#9679;
                </span>
                <span className="loading-text-animation animation-delay-1 text-screen-sm">
                  &#9679;
                </span>
                <span className="loading-text-animation animation-delay-1 text-screen-sm">
                  &#9679;
                </span>
              </>
            ) : (
              t("RODIN_GALLERY_HEAD_MENU_TIPS_5")
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapper}
      className={
        "flex relative items-center rounded-full mr-3 cursor-pointer " +
        styles.wrapper +
        " " +
        styles.limitWrapper
      }
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event?.dataTransfer?.files) {
          handleFile(event.dataTransfer.files);
        }
        wrapper.current.classList.remove(styles.fileDrop);
      }}
      onDragOver={handleDragOver}
      onDragEnter={handlerDragEnter}
      onDragLeave={handlerDragLeave}
    >
      <input
        multiple
        type="file"
        ref={fileInputRef}
        className="hidden"
        size={1}
        onChange={handleFileInputChange}
        accept=".jpg, .jpeg, .png"
      />
      <div
        className={
          "flex relative w-[42px] h-[42px] justify-center items-center rounded-full bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.15)] " +
          styles.searchIcon
        }
        onClick={onExpand}
      >
        <img
          src={SliceIcon}
          className="absolute"
          alt="Search"
        />
      </div>
      <input
        ref={inputRef}
        onPaste={(e) => {
          if (e?.clipboardData?.files.length) {
            handleFile(e.clipboardData.files);
          }
        }}
        onKeyDown={(e) => {
          if (e.code === "Enter") {
            if (!logInfo) {
              window.removeEventListener("pointerdown", onFold);
              wrapper.current.classList.add("hidden");
              setTimeout(() => {
                wrapper.current &&
                  wrapper.current.classList.remove(styles.expanded);
                wrapper.current && wrapper.current.classList.remove("hidden");
              }, 1000);
              setShowLogin(true);
              return;
            }
            handleSearchKeywords(
              (e.target as EventTarget & HTMLInputElement).value
            );
          }
        }}
        placeholder={t("RODIN_SEARCH_PLACEHOLDER")}
        className={
          "hidden flex-1 w-0 placeholder:font-normal placeholder:text-[rgba(255,255,255,0.4)] " +
          styles.input
        }
      />
      <div
        className={
          "hidden px-[18px] items-center bg-[rgba(255,255,255,0.1)] h-8 rounded-[22px] absolute right-[3px] " +
          styles.button
        }
        onMouseEnter={onPointerIn}
        onMouseLeave={onPointerOut}
        onClick={onUpload}
      >
        {searching ? (
          <>
            <span className="loading-text-animation text-screen-sm">
              &#9679;
            </span>
            <span className="loading-text-animation animation-delay-1 text-screen-sm">
              &#9679;
            </span>
            <span className="loading-text-animation animation-delay-1 text-screen-sm">
              &#9679;
            </span>
          </>
        ) : (
          <>
            <img
              src={PhotoIcon}
              className={styles.photoIcon}
            />
            <div className={"hidden text-white " + styles.upload}>
              {t("THREEWRAPPER_UPLOAD")}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default forwardRef(ModelSearchBox);
